import type { ReactNode } from 'react'

import { useRouter } from 'next/compat/router'
import { HiChevronDown, HiChevronUp } from 'react-icons/hi'

import { Box, Flex, HStack, SkeletonCircle, Spacer } from '@chakra-ui/react'

import {
  DeviceHealthPopover,
  DrawerBreadcrumb,
  DrawerTextClipboard,
} from '@/components/ui'
import { IncidentQueryResult } from '@/graphql/generated/hooks'
import { DeviceStatus } from '@/graphql/generated/schemas'

import { getOrigin } from '../utils/helpers'
import { DrawerCloseButton } from './DrawerCloseButton'
import { IncidentStatusBadge } from './IncidentStatusBadge'

type NavButtonProps = {
  ariaLabel: string
  canNavigate: boolean
  testId: string
  onClick?: () => void
  children: ReactNode
}

const NavButton = ({
  ariaLabel,
  canNavigate,
  testId,
  onClick,
  children,
}: NavButtonProps) => (
  <Box
    _active={{ bg: canNavigate ? 'gray.200' : 'transparent' }}
    _hover={{ bg: canNavigate ? 'gray.100' : 'transparent' }}
    alignItems='center'
    aria-label={ariaLabel}
    as='button'
    cursor={canNavigate ? 'pointer' : 'not-allowed'}
    data-testid={testId}
    disabled={!canNavigate}
    display='flex'
    h='28px'
    justifyContent='center'
    minWidth='28px'
    onClick={onClick}
    opacity={canNavigate ? 1 : 0.4}
    type='button'
    w='28px'
  >
    {children}
  </Box>
)

interface IncidentDrawerHeaderIProps {
  incident: IncidentQueryResult['data']['incident']
  isLoading: boolean
  onClose: () => void
  onNavigatePrevious?: () => void
  onNavigateNext?: () => void
  canNavigatePrevious?: boolean
  canNavigateNext?: boolean
  showNavigation?: boolean
}

export const IncidentDrawerHeader = ({
  incident,
  isLoading,
  onClose,
  onNavigatePrevious,
  onNavigateNext,
  canNavigatePrevious = false,
  canNavigateNext = false,
  showNavigation = false,
}: IncidentDrawerHeaderIProps) => {
  const origin = getOrigin()
  const { query } = useRouter()
  const device = incident?.devices?.[0]
  const displayId = incident?.displayId ?? (query?.displayId as string)
  const incidentLink =
    origin && displayId ? `${origin}/incidents/?displayId=${displayId}` : ''

  return (
    <Flex bg='white' h='64px' w='full'>
      <HStack gap='8px' overflow='hidden' pl='24px'>
        <DrawerCloseButton
          ariaLabel='close incident drawer'
          onClick={onClose}
        />
        {showNavigation && (
          <HStack
            border='1px solid'
            borderColor='gray.200'
            borderRadius='md'
            spacing='2px'
          >
            <NavButton
              ariaLabel='previous incident'
              canNavigate={canNavigatePrevious}
              onClick={onNavigatePrevious}
              testId='incidentDrawer_header_previousIncidentButton'
            >
              <HiChevronUp size={18} />
            </NavButton>
            <NavButton
              ariaLabel='next incident'
              canNavigate={canNavigateNext}
              onClick={onNavigateNext}
              testId='incidentDrawer_header_nextIncidentButton'
            >
              <HiChevronDown size={18} />
            </NavButton>
          </HStack>
        )}
        <DrawerBreadcrumb
          dataTestId='incidentDrawer_header_incidentName'
          isLoading={isLoading}
          label={incident?.name}
          maxWidth='175px'
        />
        {/* Hacky way to get around uneven spacing around the divider that occurs before this */}
        <Box paddingLeft={1}>
          <DrawerTextClipboard
            copyMessage='Copy link to incident'
            fontWeight='normal'
            isLoading={isLoading}
            label={displayId}
            link={incidentLink}
          />
        </Box>
        {!isLoading && <IncidentStatusBadge incident={incident} />}
      </HStack>
      <Spacer />
      {device && (
        <HStack>
          {isLoading ? (
            <SkeletonCircle size='30px' />
          ) : (
            <DeviceHealthPopover
              deviceIsUnhealthy={device?.status === DeviceStatus.Unhealthy}
            />
          )}
        </HStack>
      )}
    </Flex>
  )
}
